"use client";

import { useRef, useEffect, useState, type RefObject } from "react";
import { Container, Title, Text, Button, Group, Box } from "@forestsync/ui"
import { TbArrowRight } from "react-icons/tb";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Carousel as MantineCarousel } from "@forestsync/ui";
import { ProjectCard } from "../shared/ProjectCard";
import type { SinkWithStats } from "@/lib/queries";
import { ExploreCard } from "../shared/ExploreCard";
import { useAnalytics } from "@/components/analytics/AnalyticsProvider"

import type { LandingMetrics } from "@/actions/metrics"
import { LandingHero } from "./LandingHero"
import { LandingPathways } from "./LandingPathways"
import { LandingStatsBar } from "./LandingStatsBar"
import { LandingHowItWorks } from "./LandingHowItWorks"
import { LandingTrustBand } from "./LandingTrustBand"
import { LandingMarketActivity } from "./LandingMarketActivity"

const PREVIEW_LIMIT = 11
const REGISTRY_URL = process.env.NEXT_PUBLIC_REGISTRY_URL ?? "http://localhost:4100"

interface LandingViewProps {
  sinks: SinkWithStats[]
  metrics: LandingMetrics
}

/** Hook for scroll-reveal animation using IntersectionObserver */
function useScrollReveal<T extends HTMLElement>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

/** Wrapper component for scroll-reveal sections */
function RevealSection({ children, delay = 0, style }: { children: React.ReactNode; delay?: number; style?: React.CSSProperties }) {
  const [ref, visible] = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      data-visible={visible}
      className="reveal-section"
      style={{ "--reveal-delay": `${delay}ms`, ...style } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

export function LandingView({ sinks, metrics }: LandingViewProps) {
  const t = useTranslations("landing");
  const { trackEvent } = useAnalytics();

  const items: Array<SinkWithStats | "explore"> = [...sinks.slice(0, PREVIEW_LIMIT), "explore"]

  const traded = sinks
    .filter((s) => s.lastPrice != null)
    .sort((a, b) => (b.lastTradeAt ? new Date(b.lastTradeAt).getTime() : 0) - (a.lastTradeAt ? new Date(a.lastTradeAt).getTime() : 0))

  return (
    <Box bg="var(--mantine-color-body)" mih="100vh">
      {/* Section 1 — Hero */}
      <LandingHero />

      {/* Section 2 — Three Pathways */}
      <RevealSection>
        <LandingPathways />
      </RevealSection>

      {/* Section 3 — Stats Bar */}
      <RevealSection>
        <LandingStatsBar metrics={metrics} />
      </RevealSection>

      {/* Section 4 — How It Works */}
      <RevealSection>
        <LandingHowItWorks />
      </RevealSection>

      {/* Section 5 — Active Markets (carousel) */}
      <RevealSection>
        <Box id="projects" py={80} bg="var(--mantine-color-default-hover)">
          <Container size="lg">
            <Group justify="space-between" mb="xl" wrap="nowrap">
              <Title order={2}>{t("projects")}</Title>
              <Button
                variant="subtle"
                color="green"
                rightSection={<TbArrowRight size={16} />}
                component={Link}
                href="/market"
                onClick={() => trackEvent({ name: "cta_click", cta_location: "carousel", cta_text: "view_all" })}
              >
                {t("viewAllMarkets")}
              </Button>
            </Group>
            {sinks.length === 0 ? (
              <Text c="dimmed" ta="center">{t("noProjects")}</Text>
            ) : (
              <Box px={{ base: 0, md: 48 }} pb={{ base: 32, md: 40 }}>
                <MantineCarousel
                  slideSize={{ base: "100%", sm: "50%", lg: "33.333%" }}
                  slideGap="md"
                  withIndicators
                  controlSize={32}
                  classNames={{
                    controls: "md:!-left-16 md:!-right-16",
                    indicators: "md:!-bottom-8 !bottom-0"
                  }}
                  emblaOptions={{ align: "start", dragFree: true, loop: false }}
                >
                  {items.map((item) => {
                    if (item === "explore") {
                      return (
                        <MantineCarousel.Slide key="explore">
                          <ExploreCard projectCount={sinks.length} />
                        </MantineCarousel.Slide>
                      )
                    }
                    const sink = item
                    return (
                      <MantineCarousel.Slide key={String(sink.id)}>
                        <ProjectCard sink={sink} marketVintage={sink.marketVintage} vintageCount={sink.vintageCount} />
                      </MantineCarousel.Slide>
                    )
                  })}
                </MantineCarousel>
              </Box>
            )}
          </Container>
        </Box>
      </RevealSection>

      {/* Section 6 — Market Activity (recent trades, blurred rows 4-5) */}
      <RevealSection>
        <LandingMarketActivity traded={traded} />
      </RevealSection>

      {/* Section 7 — Trust Band */}
      <RevealSection>
        <LandingTrustBand />
      </RevealSection>

      {/* Section 8 — Ecosystem Banner */}
      <RevealSection>
        <Box py={60} className="ecosystem-banner">
          <Container size="md">
            <Title order={2} ta="center" mb="md" className="text-white">
              {t("ecosystem.headline")}
            </Title>
            <Text ta="center" size="lg" mb="xl" className="text-white/80">
              {t("ecosystem.description")}
            </Text>
            <Group justify="center" gap="md">
              <Button
                component="a"
                href={REGISTRY_URL}
                variant="white"
                color="dark"
                size="md"
                autoContrast
              >
                {t("ecosystem.registryCta")}
              </Button>
              <Button
                component={Link}
                href="/market"
                variant="outline"
                color="white"
                size="md"
              >
                {t("ecosystem.tradeCta")}
              </Button>
            </Group>
          </Container>
        </Box>
      </RevealSection>

      {/* CSS animations */}
      <style>{`
        .reveal-section {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 600ms ease-out var(--reveal-delay, 0ms), transform 600ms ease-out var(--reveal-delay, 0ms);
        }
        .reveal-section[data-visible="true"] {
          opacity: 1;
          transform: translateY(0);
        }
        .ecosystem-banner {
          background: linear-gradient(135deg, var(--mantine-color-forest-8), var(--mantine-color-forest-7));
        }
      `}</style>
    </Box>
  );
}
